/**
 * "Apply Material Preset" picker — surfaces all of the user's other
 * renders that have BOM data + lets them 1-click copy onto a target
 * render (free, no Gemini call).
 *
 * Why a separate component: keeps the materials logic out of the
 * UserDashboard. The dialog is self-contained — receives `target` and
 * `gallery`, posts to the new `/studio/materials/{target_id}/copy-from
 * /{source_id}` endpoint, then calls `onApplied(source_id, materials)`
 * so the dashboard can patch its gallery state.
 */
import { useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, X, Loader2, Check } from "lucide-react";
import { renderImageUrl } from "@/lib/imageUrl";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";

const BACKEND = getBackendUrl();

export default function MaterialPresetPicker({ target, gallery, onClose, onApplied }) {
  const [picking, setPicking] = useState(null);  // source id currently being applied

  const sources = (gallery || [])
    .filter((g) => g.id !== target.id && g.materials)
    .slice(0, 24);

  const apply = async (source) => {
    setPicking(source.id);
    try {
      const res = await axios.post(
        `${API}/studio/materials/${target.id}/copy-from/${source.id}`,
      );
      toast.success("Materials preset applied.");
      onApplied(source.id, res.data?.materials);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't apply preset."));
    } finally {
      setPicking(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="material-preset-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[88vh] rounded-2xl bg-[#0E0E0E] border border-white/10 p-6 sm:p-8 overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 text-white/55 hover:text-white flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
          <Wand2 className="w-3 h-3" /> Apply material preset
        </div>
        <h2 className="font-display text-2xl text-white mt-1">
          Copy a BOM from another render
        </h2>
        <p className="text-sm text-white/55 mt-1.5 max-w-xl">
          Pick any render below — its gold karat, diamonds, gemstones &amp;
          price range will be copied to this render instantly. Free, no
          AI call. You can still edit afterwards.
        </p>

        {sources.length === 0 ? (
          <div className="mt-8 py-12 text-center text-sm text-white/45 border border-dashed border-white/10 rounded-xl" data-testid="material-preset-empty">
            None of your other renders have BOM data yet — run Materials
            on at least one render first to use it as a preset.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6" data-testid="material-preset-grid">
            {sources.map((g) => {
              const summary = g.materials?.summary || g.materials?.gold?.purity || "Custom BOM";
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => apply(g)}
                  disabled={!!picking}
                  data-testid={`material-preset-source-${g.id}`}
                  style={{ aspectRatio: "1 / 1" }}
                  className="relative block w-full rounded-lg overflow-hidden border border-white/10 hover:border-[#D4AF37]/55 ring-1 ring-transparent hover:ring-[#D4AF37]/25 transition group bg-[#0A0A0A] disabled:opacity-40"
                  title={g.materials?.summary || g.prompt}
                >
                  <img
                    src={renderImageUrl(BACKEND, g)}
                    alt={g.prompt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
                  <span className="absolute bottom-1.5 left-2 right-2 text-[9.5px] uppercase tracking-[0.16em] text-[#D4AF37] font-medium leading-tight line-clamp-1">
                    {summary}
                  </span>
                  {picking === g.id && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                    </div>
                  )}
                  {!picking && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition flex items-center justify-center pointer-events-none">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-black px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition">
                        <Check className="w-3 h-3" /> Apply
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
